import React, { useEffect, useMemo, useState } from 'react'
import { Alert, Badge, Button, Card, Checkbox, Collapse, Modal, Segmented, Select, Spin, Switch, Tag, message, theme } from 'antd'
import type { CollapseProps } from 'antd'
import { SaveOutlined, ReloadOutlined } from '@ant-design/icons'
import axiosInstance from '@/lib/axios'

// Vista "per ruolo" della stessa matrice mostrata da RolePermissionsMatrix:
// si guarda un ruolo alla volta invece della griglia permessi x ruoli.
// Stessa fonte dati (GET /permissions/matrix) e stesso salvataggio.

interface Role {
  id: number
  codice: string
  nome: string
}

interface Permission {
  id: number
  codice: string
  nome: string
  categoria: string
  descrizione: string
  attivo: boolean
  roles: {
    ruolo_id: number
    ruolo_codice: string
    granted: boolean
  }[]
}

// L'amministratore scavalca require_permission nel backend: le sue spunte
// non descrivono cosa puo' fare, quindi qui sono in sola lettura.
const ADMIN_ROLE_ID = 1

export const PermissionsByRole: React.FC<{ refreshKey?: number }> = ({ refreshKey = 0 }) => {
  const { token } = theme.useToken()
  const [permissions, setPermissions] = useState<Permission[]>([])
  const [roles, setRoles] = useState<Role[]>([])
  const [selectedRoleId, setSelectedRoleId] = useState<number | null>(null)
  const [changes, setChanges] = useState<{ [key: string]: boolean }>({})
  const [showCodes, setShowCodes] = useState(false)
  const [showInactive, setShowInactive] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [vista, setVista] = useState<'ruolo' | 'confronto'>('ruolo')
  const [roleAId, setRoleAId] = useState<number | null>(null)
  const [roleBId, setRoleBId] = useState<number | null>(null)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    fetchMatrix()
  }, [refreshKey])

  // includeInactive si passa esplicito perche' la spunta lo cambia e rilegge
  // nello stesso gesto: leggerlo dallo stato darebbe il valore precedente.
  // resetChanges=false quando a rileggere e' la spunta: cambiare cosa si vede
  // non deve buttare via le modifiche gia' fatte.
  const fetchMatrix = async (includeInactive = showInactive, resetChanges = true) => {
    try {
      setLoading(true)
      const response = await axiosInstance.get('/permissions/matrix', {
        params: includeInactive ? { include_inactive: true } : undefined,
      })
      const loadedRoles: Role[] = response.data.roles
      setPermissions(response.data.permissions)
      setRoles(loadedRoles)
      if (resetChanges) setChanges({})
      setSelectedRoleId(prev =>
        prev !== null && loadedRoles.some(r => r.id === prev)
          ? prev
          : loadedRoles[0]?.id ?? null
      )
      const primoValido = (prev: number | null, indice: number) =>
        prev !== null && loadedRoles.some(r => r.id === prev) ? prev : loadedRoles[indice]?.id ?? null
      setRoleAId(prev => primoValido(prev, 0))
      setRoleBId(prev => primoValido(prev, 1))
    } catch (error) {
      message.error('Errore nel caricamento dei permessi')
      console.error(error)
    } finally {
      setLoading(false)
    }
  }

  // Valore corrente: la modifica pendente se c'e', altrimenti il dato del server.
  const getPermissionValue = (perm: Permission, role: Role): boolean => {
    const key = `${role.codice}-${perm.id}`
    if (Object.prototype.hasOwnProperty.call(changes, key)) {
      return changes[key]
    }
    const roleData = perm.roles.find(r => r.ruolo_codice === role.codice)
    return roleData?.granted ?? false
  }

  // Il delta tiene solo le differenze: tornare al valore di partenza
  // rimuove la voce invece di registrare una modifica nulla.
  const handleToggle = (perm: Permission, role: Role, newValue: boolean) => {
    const key = `${role.codice}-${perm.id}`
    const originalGranted = perm.roles.find(r => r.ruolo_codice === role.codice)?.granted ?? false
    setChanges(prev => {
      if (newValue === originalGranted) {
        const next = { ...prev }
        delete next[key]
        return next
      }
      return { ...prev, [key]: newValue }
    })
  }

  // Il PUT porta solo i ruoli che hanno almeno una modifica pendente.
  // update_permissions_matrix fa un upsert riga per riga e non tocca i ruoli
  // assenti dal payload: gli altri restano come sono.
  const eseguiSalvataggio = async () => {
    if (ruoliConModifiche.length === 0) return
    try {
      setSaving(true)
      const payload: { [key: string]: { [key: string]: boolean } } = {}
      for (const role of ruoliConModifiche) {
        const permessiDelRuolo: { [key: string]: boolean } = {}
        for (const perm of permissions) {
          permessiDelRuolo[perm.id.toString()] = getPermissionValue(perm, role)
        }
        payload[role.id.toString()] = permessiDelRuolo
      }
      await axiosInstance.put('/permissions/matrix', payload)
      setConfirmOpen(false)
      message.success(
        ruoliConModifiche.length === 1
          ? `Permessi di ${ruoliConModifiche[0].nome} aggiornati con successo`
          : `Permessi aggiornati su ${ruoliConModifiche.length} ruoli`
      )
      setChanges({})
      await fetchMatrix()
    } catch (error) {
      message.error('Errore nel salvataggio dei permessi')
      console.error(error)
    } finally {
      setSaving(false)
    }
  }

  // Scrivere piu' ruoli in un colpo solo merita una conferma esplicita:
  // le modifiche degli altri ruoli non sono sotto gli occhi di chi salva.
  const handleSaveClick = () => {
    if (ruoliConModifiche.length > 1) setConfirmOpen(true)
    else void eseguiSalvataggio()
  }

  const selectedRole = roles.find(r => r.id === selectedRoleId) ?? null
  const isAdminRole = selectedRole?.id === ADMIN_ROLE_ID
  const changeCount = Object.keys(changes).length
  const hasChanges = changeCount > 0

  // Modifiche pendenti per ruolo: marcano i chip e alimentano il bottone Salva.
  // Il trattino nel prefisso evita che GM- prenda anche le chiavi di GMA.
  const pendingByRoleId = useMemo(() => {
    const counts: { [roleId: number]: number } = {}
    for (const role of roles) {
      const prefisso = `${role.codice}-`
      counts[role.id] = Object.keys(changes).filter(key => key.startsWith(prefisso)).length
    }
    return counts
  }, [roles, changes])

  const permessiDisattivati = permissions.filter(p => !p.attivo).length

  // I ruoli che finiranno nel PUT, nell'ordine in cui compaiono nei chip.
  const ruoliConModifiche = roles.filter(role => (pendingByRoleId[role.id] ?? 0) > 0)

  // Conteggio per i chip: si contano i granted veri del payload della matrice,
  // non _count.permissions di /permissions/roles (conta le righe, granted
  // compreso, quindi vale il totale del catalogo per ogni ruolo).
  const countsByRoleId = useMemo(() => {
    const counts: { [roleId: number]: number } = {}
    for (const role of roles) {
      counts[role.id] = permissions.filter(p => getPermissionValue(p, role)).length
    }
    return counts
  }, [roles, permissions, changes])

  // ---- Confronto fra due ruoli (sola lettura) ----
  const roleA = roles.find(r => r.id === roleAId) ?? null
  const roleB = roles.find(r => r.id === roleBId) ?? null
  const opzioniRuoli = roles.map(role => ({ value: role.id, label: `${role.nome} (${role.codice})` }))

  // Solo i permessi su cui i due ruoli non coincidono. Si legge con lo stesso
  // getPermissionValue della vista per ruolo, quindi il confronto tiene conto
  // anche delle modifiche ancora da salvare.
  const differenze = useMemo(() => {
    if (!roleA || !roleB || roleA.id === roleB.id) return []
    return permissions.filter(p => getPermissionValue(p, roleA) !== getPermissionValue(p, roleB))
  }, [permissions, roleA, roleB, changes])

  const categorieDifferenze = useMemo(() => {
    const gruppi = new Map<string, Permission[]>()
    for (const perm of differenze) {
      const elenco = gruppi.get(perm.categoria)
      if (elenco) elenco.push(perm)
      else gruppi.set(perm.categoria, [perm])
    }
    return Array.from(gruppi.entries())
  }, [differenze])

  // Raggruppamento per il campo categoria del payload, nell'ordine
  // in cui il backend lo restituisce (ORDER BY categoria, codice).
  const categorie = useMemo(() => {
    const gruppi = new Map<string, Permission[]>()
    for (const perm of permissions) {
      const elenco = gruppi.get(perm.categoria)
      if (elenco) elenco.push(perm)
      else gruppi.set(perm.categoria, [perm])
    }
    return Array.from(gruppi.entries())
  }, [permissions])

  const renderPermissionRow = (perm: Permission, role: Role) => {
    const attivo = getPermissionValue(perm, role)
    const modificato = Object.prototype.hasOwnProperty.call(changes, `${role.codice}-${perm.id}`)
    // Un permesso disattivato non e' consultato da nessun endpoint: mostrarlo
    // serve a vedere le assegnazioni rimaste, non a cambiarle.
    const bloccato = isAdminRole || !perm.attivo
    return (
      <div
        key={perm.id}
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 12,
          padding: '10px 4px',
          borderTop: `1px solid ${token.colorBorderSecondary}`,
        }}
      >
        <Switch
          size="small"
          checked={attivo}
          disabled={bloccato}
          onChange={value => handleToggle(perm, role, value)}
          style={{ marginTop: 2, flexShrink: 0 }}
        />
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              fontSize: 13,
              fontWeight: 500,
              color: perm.attivo ? token.colorText : token.colorTextTertiary,
            }}
          >
            {perm.nome}
            {!perm.attivo && (
              <Tag color="default" style={{ marginLeft: 6, fontSize: 10 }}>disattivato</Tag>
            )}
            {modificato && (
              <Tag color="blue" style={{ marginLeft: 6, fontSize: 10 }}>modificato</Tag>
            )}
          </div>
          {perm.descrizione && (
            <div style={{ fontSize: 12, color: token.colorTextSecondary, marginTop: 2 }}>
              {perm.descrizione}
            </div>
          )}
          {showCodes && (
            <div
              style={{
                fontSize: 11,
                marginTop: 2,
                color: token.colorTextTertiary,
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
              }}
            >
              {perm.codice}
            </div>
          )}
        </div>
      </div>
    )
  }

  const renderDiffRow = (perm: Permission, a: Role, b: Role) => {
    const haA = getPermissionValue(perm, a)
    const haB = getPermissionValue(perm, b)
    const segno = (ha: boolean) => (
      <span
        style={{
          textAlign: 'center',
          fontSize: 12,
          fontWeight: ha ? 600 : 400,
          color: ha ? token.colorSuccess : token.colorTextQuaternary,
        }}
      >
        {ha ? 'si' : '—'}
      </span>
    )
    return (
      <div
        key={perm.id}
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 72px 72px',
          alignItems: 'center',
          gap: 8,
          padding: '8px 4px',
          borderTop: `1px solid ${token.colorBorderSecondary}`,
        }}
      >
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 500, color: token.colorText }}>{perm.nome}</div>
          {perm.descrizione && (
            <div style={{ fontSize: 12, color: token.colorTextSecondary, marginTop: 2 }}>
              {perm.descrizione}
            </div>
          )}
          {showCodes && (
            <div
              style={{
                fontSize: 11,
                marginTop: 2,
                color: token.colorTextTertiary,
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
              }}
            >
              {perm.codice}
            </div>
          )}
        </div>
        {segno(haA)}
        {segno(haB)}
      </div>
    )
  }

  const collapseItems: CollapseProps['items'] = selectedRole
    ? categorie.map(([categoria, permessi]) => {
        const attiviNellaCategoria = permessi.filter(p => getPermissionValue(p, selectedRole)).length
        return {
          key: categoria,
          label: <span style={{ fontSize: 13, fontWeight: 600 }}>{categoria}</span>,
          extra: (
            <span style={{ fontSize: 12, color: token.colorTextTertiary }}>
              {attiviNellaCategoria} di {permessi.length}
            </span>
          ),
          children: (
            <div>{permessi.map(perm => renderPermissionRow(perm, selectedRole))}</div>
          ),
        }
      })
    : []

  return (
    <div>
      {/* Segmented invece di Tabs: dentro SettingsPage siamo gia' in un Tabs,
          un secondo livello di linguette confonderebbe i due piani. */}
      <Segmented
        size="small"
        style={{ marginBottom: 12 }}
        value={vista}
        onChange={value => setVista(value as 'ruolo' | 'confronto')}
        options={[
          { label: 'Per ruolo', value: 'ruolo' },
          { label: 'Confronta due ruoli', value: 'confronto' },
        ]}
      />

      {/* Barra di controllo — fuori dai chip, sempre visibile */}
      <div style={{ marginBottom: 12, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <Button icon={<ReloadOutlined />} onClick={() => fetchMatrix()} loading={loading} size="small">
          Ricarica
        </Button>
        <Button
          type="primary"
          icon={<SaveOutlined />}
          onClick={handleSaveClick}
          loading={saving}
          disabled={!hasChanges}
          size="small"
        >
          Salva {hasChanges ? `(${changeCount})` : ''}
        </Button>
        <Checkbox checked={showCodes} onChange={e => setShowCodes(e.target.checked)}>
          <span style={{ fontSize: 12 }}>Mostra i codici tecnici</span>
        </Checkbox>
        <Checkbox
          checked={showInactive}
          disabled={loading}
          onChange={e => {
            const valore = e.target.checked
            setShowInactive(valore)
            void fetchMatrix(valore, false)
          }}
        >
          <span style={{ fontSize: 12 }}>Mostra anche i permessi disattivati</span>
        </Checkbox>
      </div>

      {hasChanges && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message={`${changeCount} modifica/he non salvata/e`}
        />
      )}

      {showInactive && vista === 'ruolo' && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message={`Stai vedendo anche i ${permessiDisattivati} permessi disattivati`}
          description="I conteggi sui chip e il totale del ruolo li includono. Un permesso disattivato non e' consultato da nessun endpoint: le sue spunte sono in sola lettura."
        />
      )}

      <Spin spinning={loading}>
        {vista === 'ruolo' && (
          <>
          {/* Un chip per ruolo, con i permessi attivi calcolati dal payload */}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
            {roles.map(role => {
              const selezionato = role.id === selectedRoleId
              const pendenti = pendingByRoleId[role.id] ?? 0
              return (
                <Badge
                  key={role.id}
                  count={pendenti}
                  size="small"
                  offset={[-4, 2]}
                  title={pendenti > 0 ? `${pendenti} modifica/he non salvata/e su ${role.nome}` : undefined}
                >
                <Button
                  size="small"
                  type={selezionato ? 'primary' : 'default'}
                  onClick={() => setSelectedRoleId(role.id)}
                  title={role.nome}
                  style={{ fontWeight: selezionato ? 600 : 400 }}
                >
                  {role.codice}
                  <span
                    style={{
                      marginLeft: 6,
                      fontSize: 11,
                      color: selezionato ? undefined : token.colorTextTertiary,
                    }}
                  >
                    {countsByRoleId[role.id] ?? 0}
                  </span>
                </Button>
                </Badge>
              )
            })}
          </div>

          {selectedRole && (
            <Card size="small" bodyStyle={{ padding: '12px 16px' }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'baseline',
                  gap: 8,
                  flexWrap: 'wrap',
                  marginBottom: 4,
                }}
              >
                <span style={{ fontSize: 16, fontWeight: 600 }}>{selectedRole.nome}</span>
                <Tag style={{ fontSize: 11 }}>{selectedRole.codice}</Tag>
                <span style={{ marginLeft: 'auto', fontSize: 12, color: token.colorTextSecondary }}>
                  {countsByRoleId[selectedRole.id] ?? 0} permessi attivi su {permissions.length}
                  {showInactive && ' (disattivati inclusi)'}
                </span>
              </div>

              {isAdminRole && (
                <Alert
                  type="info"
                  showIcon
                  style={{ margin: '12px 0' }}
                  message="L'amministratore scavalca il controllo dei permessi"
                  description="Il ruolo con ruolo_id 1 passa comunque require_permission nel backend: queste spunte non hanno effetto sul suo accesso. Per provare una configurazione serve un altro ruolo."
                />
              )}

              {/* La key rimonta il Collapse quando cambia l'insieme delle
                  categorie: defaultActiveKey vale solo al mount, altrimenti
                  una categoria comparsa dopo (es. mostrando i disattivati)
                  resterebbe chiusa mentre tutte le altre sono aperte. */}
              <Collapse
                key={categorie.map(([categoria]) => categoria).join('|')}
                ghost
                size="small"
                items={collapseItems}
                defaultActiveKey={categorie.map(([categoria]) => categoria)}
              />
            </Card>
          )}
          </>
        )}

        {vista === 'confronto' && roleA && roleB && (
          <Card size="small" bodyStyle={{ padding: '12px 16px' }}>
            <div
              style={{
                display: 'flex',
                gap: 8,
                alignItems: 'center',
                flexWrap: 'wrap',
                marginBottom: 12,
              }}
            >
              <Select
                size="small"
                style={{ minWidth: 210 }}
                value={roleA.id}
                onChange={value => setRoleAId(value)}
                options={opzioniRuoli}
              />
              <span style={{ fontSize: 12, color: token.colorTextTertiary }}>rispetto a</span>
              <Select
                size="small"
                style={{ minWidth: 210 }}
                value={roleB.id}
                onChange={value => setRoleBId(value)}
                options={opzioniRuoli}
              />
              <span style={{ marginLeft: 'auto', fontSize: 12, color: token.colorTextSecondary }}>
                {differenze.length} {differenze.length === 1 ? 'differenza' : 'differenze'} —{' '}
                {countsByRoleId[roleA.id] ?? 0} contro {countsByRoleId[roleB.id] ?? 0} permessi
              </span>
            </div>

            {differenze.length === 0 ? (
              <div
                style={{
                  padding: '28px 0',
                  textAlign: 'center',
                  fontSize: 13,
                  color: token.colorTextSecondary,
                }}
              >
                {roleA.id === roleB.id
                  ? `Hai scelto due volte ${roleA.nome}: scegline un altro per vedere le differenze.`
                  : `${roleA.nome} e ${roleB.nome} hanno esattamente gli stessi permessi.`}
              </div>
            ) : (
              <div>
                {/* Intestazione delle due colonne */}
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 72px 72px',
                    gap: 8,
                    padding: '4px 4px 8px',
                    fontSize: 12,
                    fontWeight: 600,
                    color: token.colorTextSecondary,
                  }}
                >
                  <span>Permesso</span>
                  <span style={{ textAlign: 'center' }}>{roleA.codice}</span>
                  <span style={{ textAlign: 'center' }}>{roleB.codice}</span>
                </div>

                {categorieDifferenze.map(([categoria, permessi]) => (
                  <div key={categoria} style={{ marginTop: 12 }}>
                    <div
                      style={{
                        fontSize: 13,
                        fontWeight: 600,
                        color: token.colorText,
                        padding: '4px 4px',
                        background: token.colorFillAlter,
                        borderRadius: token.borderRadiusSM,
                      }}
                    >
                      {categoria}
                      <span
                        style={{
                          marginLeft: 8,
                          fontSize: 12,
                          fontWeight: 400,
                          color: token.colorTextTertiary,
                        }}
                      >
                        {permessi.length} {permessi.length === 1 ? 'differenza' : 'differenze'}
                      </span>
                    </div>
                    {permessi.map(perm => renderDiffRow(perm, roleA, roleB))}
                  </div>
                ))}
              </div>
            )}
          </Card>
        )}
      </Spin>

      {/* Conferma esplicita quando il salvataggio tocca piu' di un ruolo */}
      <Modal
        open={confirmOpen}
        title="Salvare le modifiche di piu' ruoli?"
        okText={`Salva ${changeCount} modifiche`}
        cancelText="Annulla"
        confirmLoading={saving}
        onOk={eseguiSalvataggio}
        onCancel={() => setConfirmOpen(false)}
      >
        <div style={{ fontSize: 13, color: token.colorTextSecondary, marginBottom: 8 }}>
          Verranno scritti {ruoliConModifiche.length} ruoli. Gli altri restano invariati.
        </div>
        <ul style={{ margin: 0, paddingLeft: 20 }}>
          {ruoliConModifiche.map(role => (
            <li key={role.id} style={{ fontSize: 13, marginBottom: 2 }}>
              <strong>{role.nome}</strong> <Tag style={{ fontSize: 11 }}>{role.codice}</Tag>
              {pendingByRoleId[role.id]} modifica/he
            </li>
          ))}
        </ul>
      </Modal>
    </div>
  )
}

export default PermissionsByRole
